"use client"

import styled from "@emotion/styled"
import { useQueryClient } from "@tanstack/react-query"
import {
  isForbiddenError,
  isTRPCClientError,
  isUnauthorizedError,
} from "app/core/errors"
import { useIsKids } from "app/core/hooks/useIsKids"
import DashboardLayout from "app/core/layouts/DashboardLayout"
import {
  default as Layout,
  default as LayoutWrapper,
} from "app/core/layouts/Layout"
import PublicLayout from "app/core/layouts/PublicLayout"
import { ContentNotAvailable } from "app/errors/ContentNotAvailable"
import { NoGroups } from "app/errors/NoGroups"
import { NotAuthorized } from "app/errors/NotAuthorized"
import { RequestFailed } from "app/errors/RequestFailed"
import GroupPicker from "app/groups/components/GroupPicker"
import KidsErrorLayout from "app/kids/components/KidsErrorLayout"
import { OrganizationPickerContent } from "app/organizations/components/OrganizationPickerContent"

import MigrateGroupComponent from "app/schooljaren/components/MigrateGroupComponent"

import { UserInfoProvider } from "app/core/providers/UserInfoProvider"
import { parseAsString, useQueryState } from "nuqs"
import { ReactNode, useEffect, useRef } from "react"
import { LIGHT_GREEN_COLOR } from "theme/colors"
import Stack from "ui/Stack"
import { useIsPublic } from "web/src/core/hooks/useIsPublic"
import { useRouter } from "../hooks/useRouter"

const StackWithBackground = styled(Stack)`
  background-color: ${LIGHT_GREEN_COLOR};
`

interface ErrorFallbackProps {
  error: Error & { statusCode?: number }
  resetErrorBoundary: () => void
}

function DynamicLayout({
  children,
  title,
}: {
  children: ReactNode
  title: string
}) {
  const isPublic = useIsPublic()

  if (isPublic) {
    return (
      <UserInfoProvider>
        <PublicLayout title={title}>{children}</PublicLayout>
      </UserInfoProvider>
    )
  }
  return (
    <UserInfoProvider>
      <DashboardLayout title={title}>{children}</DashboardLayout>
    </UserInfoProvider>
  )
}

export default function RootErrorFallback({
  error,
  resetErrorBoundary,
}: ErrorFallbackProps) {
  const router = useRouter()
  const isKidsPage = useIsKids()
  const firstLoad = useRef(true)
  const [next] = useQueryState("next", parseAsString)
  const qc = useQueryClient()

  useEffect(() => {
    if (
      isTRPCClientError(error) &&
      error.data?.code === "MULTIPLE_SESSIONS" &&
      !isKidsPage
    ) {
      if (firstLoad.current) {
        firstLoad.current = false
        const nextParam = next?.includes("meerdere-sessies")
          ? undefined
          : next
            ? next
            : router.pathname === "/" || router.pathname === "%2F"
              ? undefined
              : router.pathname
        router.push(
          nextParam
            ? `/meerdere-sessies?next=${nextParam}`
            : "/meerdere-sessies"
        )
      }
      return
    }
    if (isUnauthorizedError(error)) {
      if (firstLoad.current) {
        firstLoad.current = false
        const loginPath = isKidsPage
          ? `/login?next=${router.pathname}`
          : `/login${next === "meerdere-sessies" ? "" : next ? `?next=${next}` : ""}${router.pathname !== "/" ? (next ? "&multipleSessions=true" : "?multipleSessions=true") : ""}`
        router.push(loginPath)
      }
      return
    }
  }, [error, resetErrorBoundary, router, next, isKidsPage])

  if (
    isUnauthorizedError(error) ||
    (isTRPCClientError(error) && error.data?.code === "MULTIPLE_SESSIONS")
  ) {
    return null
  }

  if (isKidsPage) {
    return (
      <LayoutWrapper title="123ZING Kids">
        <KidsErrorLayout />
      </LayoutWrapper>
    )
  }

  if (isTRPCClientError(error) && error.data?.code === "SHOW_GROUP_MIGRATION") {
    return (
      <MigrateGroupComponent
        onComplete={async () => {
          await qc.invalidateQueries()
          resetErrorBoundary()
        }}
      />
    )
  }

  if (
    isTRPCClientError(error) &&
    error.data?.code === "NO_ORGANIZATION_SELECTED"
  ) {
    return (
      <Layout title="Schoolkeuze">
        <OrganizationPickerContent
          onChange={async () => {
            await qc.invalidateQueries()
            resetErrorBoundary()
          }}
        />
      </Layout>
    )
  } else if (
    isTRPCClientError(error) &&
    error.data?.code === "NO_GROUP_SELECTED"
  ) {
    return (
      <Layout title={"Groepskeuze"}>
        <StackWithBackground justify="center" align="stretch">
          <GroupPicker
            onChange={async () => {
              await qc.invalidateQueries()
              resetErrorBoundary()
            }}
            resetErrorBoundary={resetErrorBoundary}
          />
        </StackWithBackground>
      </Layout>
    )
  } else if (isTRPCClientError(error) && error.data?.code === "NO_GROUPS") {
    return (
      <DynamicLayout title="Geen groepen">
        <NoGroups />
      </DynamicLayout>
    )
  } else if (isForbiddenError(error)) {
    return (
      <DynamicLayout title="Foutcode 403">
        <NotAuthorized />
      </DynamicLayout>
    )
  } else if (
    isTRPCClientError(error) &&
    error.data?.code === "CONTENT_NOT_AVAILABLE"
  ) {
    return (
      // <DynamicLayout title="Content niet beschikbaar">
      <ContentNotAvailable />
      // </DynamicLayout>
    )
  } else {
    return (
      <DynamicLayout title={`Foutcode ${error.statusCode || 400}`}>
        <RequestFailed
          code={error.statusCode || 400}
          message={error.message || error.name}
        />
      </DynamicLayout>
    )
  }
}
